import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowUpRight, ArrowRight, Sparkles, Code, Palette, Video, Camera, Zap, Smartphone } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const shotlabRef = useRef(null);
  const processRef = useRef(null);

  useEffect(() => {
    document.title = 'Squarex Studios | Digital Agency & AI Production';

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo(
        '.hero-title',
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, duration: 1.2, ease: 'power4.out' }
      );
      gsap.fromTo(
        '.hero-subtitle',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, delay: 0.3, ease: 'power3.out' }
      );
      gsap.fromTo(
        '.hero-cta',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, delay: 0.6, ease: 'power3.out', stagger: 0.1 }
      );

      // Services scroll animation
      gsap.fromTo(
        '.service-card',
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: servicesRef.current,
            start: 'top 80%',
          },
        }
      );

      // ShotLab section animation
      gsap.fromTo(
        '.shotlab-content',
        { opacity: 0, x: -50 },
        {
          opacity: 1,
          x: 0,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: shotlabRef.current,
            start: 'top 70%',
          },
        }
      );

      // Process steps animation
      gsap.fromTo(
        '.process-step',
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: processRef.current,
            start: 'top 75%',
          },
        }
      );
    });

    return () => ctx.revert();
  }, []);

  const services = [
    { icon: Code, title: 'Shopify Development', desc: 'Custom storefronts that convert visitors into loyal customers.' },
    { icon: Palette, title: 'UI/UX Design', desc: 'Intuitive interfaces that delight users and drive engagement.' },
    { icon: Sparkles, title: 'Branding & Identity', desc: 'Distinctive brand systems that stand out in crowded markets.' },
    { icon: Video, title: 'Video & Motion', desc: 'Compelling video content that tells your brand story.' },
    { icon: Smartphone, title: 'iOS & macOS', desc: 'Premium native applications for the entire Apple ecosystem.' },
    { icon: Camera, title: 'AI Photo Shoots', desc: 'Studio-quality product imagery powered by AI.' },
  ];

  const process = [
    { num: '01', title: 'Discovery', desc: 'Understanding your vision and goals' },
    { num: '02', title: 'Design', desc: 'Crafting the perfect solution' },
    { num: '03', title: 'Build', desc: 'Bringing designs to life' },
    { num: '04', title: 'Launch', desc: 'Going live with confidence' },
    { num: '05', title: 'Scale', desc: 'Growing your success' },
  ];

  const testimonials = [
    {
      quote: "Squarex Studios transformed our e-commerce presence. The results exceeded all expectations.",
      author: "Sarah Chen",
      role: "CEO, Luxe Fashion",
    },
    {
      quote: "The AI photo shoots saved us thousands while delivering premium quality imagery.",
      author: "Mark Rodriguez",
      role: "Brand Manager, Organic Beauty Co",
    },
    {
      quote: "Their Shopify expertise and design sensibility are unmatched. Highly recommended.",
      author: "David Kim",
      role: "Founder, TechStart",
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative min-h-screen flex items-center pt-24 pb-20 hero-gradient grid-pattern overflow-hidden"
      >
        {/* Background effects */}
        <div className="hero-bg-effect absolute top-1/4 left-1/4 opacity-50" />
        <div className="hero-bg-effect absolute bottom-1/4 right-1/4 opacity-30" style={{ animationDelay: '-10s' }} />

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-4xl">
            <p className="hero-subtitle text-[#af0000] font-mono text-sm uppercase tracking-widest mb-6">
              Creative Agency / Shopify / AI Production
            </p>
            <h1 className="hero-title text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold font-['Syne'] tracking-tighter leading-[0.9] mb-8">
              We build brands<br />
              that <span className="text-[#af0000]">break</span> the<br />
              mold
            </h1>
            <p className="hero-subtitle text-xl md:text-2xl text-[#94a3b8] font-light max-w-2xl mb-12 leading-relaxed">
              Premium Shopify development, stunning design systems, and AI-powered visual production — all under one roof.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                data-testid="hero-view-services-btn"
                className="hero-cta btn-primary flex items-center justify-center gap-2"
              >
                View Services
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-book-consultation-btn"
                className="hero-cta btn-secondary flex items-center justify-center gap-2"
              >
                Book Consultation
                <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2 text-[#64748b]">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-px h-16 bg-gradient-to-b from-[#af0000] to-transparent" />
        </div>
      </section>

      {/* Agency Intro */}
      <section className="py-24 md:py-32 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
                Who We Are
              </p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-6">
                A creative studio for the digital age
              </h2>
              <p className="text-[#94a3b8] text-lg leading-relaxed mb-6">
                Squarex Studios is where design excellence meets technical innovation. We specialize in crafting premium Shopify experiences, building cohesive brand identities, and pioneering AI-powered visual production.
              </p>
              <p className="text-[#94a3b8] text-lg leading-relaxed">
                Our team of designers, developers, and creative technologists work together to deliver solutions that don't just look beautiful — they perform.
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-[#1a1a1a] overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Squarex Studios Team"
                  className="w-full h-full object-cover opacity-80"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border-2 border-[#af0000]" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section ref={servicesRef} className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
                What We Do
              </p>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">
                Services built for<br />modern brands
              </h2>
            </div>
            <Link
              to="/services"
              data-testid="view-all-services-btn"
              className="flex items-center gap-2 text-[#af0000] font-semibold hover:gap-4 transition-all"
            >
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="service-card group relative p-8 bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-500 overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-[#af0000]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <service.icon size={32} className="text-[#af0000] mb-6" />
                <h3 className="text-xl font-bold font-['Syne'] mb-3">{service.title}</h3>
                <p className="text-[#94a3b8] text-sm leading-relaxed">{service.desc}</p>
                <div className="absolute bottom-0 left-0 w-0 h-1 bg-[#af0000] group-hover:w-full transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SQRX ShotLab Feature */}
      <section ref={shotlabRef} className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#af0000]/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="shotlab-content">
              <div className="inline-flex items-center gap-2 bg-[#af0000]/20 text-[#af0000] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Zap size={16} />
                Featured Service
              </div>
              <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight mb-6">
                SQRX ShotLab
              </h2>
              <p className="text-2xl text-[#94a3b8] font-light mb-6">
                AI-Powered Product Photography
              </p>
              <p className="text-[#94a3b8] leading-relaxed mb-8">
                Revolutionary product visualization that creates studio-quality imagery without physical shoots. Generate lifestyle photos, campaign visuals, and brand-aligned content in days, not weeks.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                {['Studio Shots', 'Lifestyle Visuals', 'Campaign Ready', 'No Physical Shoot'].map((tag) => (
                  <span key={tag} className="px-4 py-2 border border-white/20 text-sm text-[#94a3b8]">
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to="/shotlab"
                data-testid="explore-shotlab-btn"
                className="btn-primary inline-flex items-center gap-2"
              >
                Explore ShotLab
                <ArrowUpRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="aspect-[3/4] bg-[#1a1a1a] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=533&fit=crop"
                      alt="Product Shot 1"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-square bg-[#1a1a1a] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop"
                      alt="Product Shot 2"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="aspect-square bg-[#1a1a1a] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop"
                      alt="Product Shot 3"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="aspect-[3/4] bg-[#1a1a1a] overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1526170375885-4d8ecf77b99f?w=400&h=533&fit=crop"
                      alt="Product Shot 4"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-32 h-32 border-2 border-[#af0000] -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section ref={processRef} className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
              Our Process
            </p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">
              How we bring ideas to life
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-8">
            {process.map((step, index) => (
              <div key={index} className="process-step text-center">
                <div className="text-5xl font-bold font-['Syne'] text-[#af0000]/30 mb-4">
                  {step.num}
                </div>
                <h3 className="text-xl font-bold font-['Syne'] mb-2">{step.title}</h3>
                <p className="text-[#94a3b8] text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 md:py-32 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">
              Testimonials
            </p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">
              What our clients say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((item, index) => (
              <div
                key={index}
                data-testid={`testimonial-${index}`}
                className="testimonial-card p-8 relative"
              >
                <div className="text-6xl text-[#af0000]/30 font-serif absolute top-4 left-6">"</div>
                <p className="text-[#f8fafc] leading-relaxed mb-6 pt-8 relative z-10">
                  {item.quote}
                </p>
                <div>
                  <p className="font-semibold">{item.author}</p>
                  <p className="text-[#94a3b8] text-sm">{item.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 hero-gradient" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="text-4xl md:text-6xl font-bold font-['Syne'] tracking-tight mb-6">
            Ready to start your<br />
            <span className="text-[#af0000]">next project?</span>
          </h2>
          <p className="text-xl text-[#94a3b8] mb-10 max-w-2xl mx-auto">
            Let's create something extraordinary together. Get in touch and let's discuss how we can bring your vision to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="final-cta-btn"
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              Start a Project
              <ArrowUpRight size={18} />
            </Link>
            <Link
              to="/services"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              View Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
